import { readReleaseManifest, releaseAssetPath } from './release-assets';

/**
 * Maps manifest `file` paths (relative to `frontend/assets/`) to external asset URLs.
 * Keep in sync with [public/assets/game/manifest.json](../../../../public/assets/game/manifest.json).
 *
 * When `VITE_GAME_ASSETS_BASE` is set (Docker Compose local), PNGs load from nginx
 * `/game-static/` → mounted `frontend/assets/` so client and admin share one source tree.
 */

function gameAssetsBaseUrl(): string | undefined {
  // Preserve AUTO-268's canonical build hook; BASE_URL also identifies local fixtures.
  const sha = import.meta.env.VITE_RELEASE_SHA;
  if (sha && !/^[a-f0-9]{40}$/.test(sha)) throw Error('Invalid release source SHA');
  const builtRelease = releaseAssetPath(import.meta.env.BASE_URL ?? '/');
  if (sha) {
    if (builtRelease && builtRelease.root !== `/releases/${sha}/`) throw Error('Conflicting release generation');
    return `/releases/${sha}/game/`;
  }
  if (builtRelease) return `${builtRelease.root}game/`;
  const raw = import.meta.env.VITE_GAME_ASSETS_BASE;
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  return raw.endsWith('/') ? raw : `${raw}/`;
}

function assertSafeGameAssetPath(file: string): void {
  if (!file || file.startsWith('/') || /[\\:?#%]/.test(file) || file.split('/').some(part => !part || part === '.' || part === '..')) throw Error('Unsafe game asset path');
}

function staticGameAssetUrl(manifestFile: string): string | undefined {
  assertSafeGameAssetPath(manifestFile);
  const base = gameAssetsBaseUrl();
  if (!base) return undefined;
  return `${base}${manifestFile.replace(/^\//, '')}`;
}

export function resolveGameAssetFile(manifestFile: string): string {
  const staticUrl = staticGameAssetUrl(manifestFile);
  if (staticUrl) return staticUrl;
  throw new Error(`VITE_GAME_ASSETS_BASE is required for game asset: ${manifestFile}`);
}

export function tryResolveGameAssetFile(manifestFile: string, manifest?: GameTextureManifest): string | undefined {
  assertSafeGameAssetPath(manifestFile);
  const root = manifest && manifestRoots.get(manifest);
  return root ? `${root}game/${manifestFile.replace(/^\//, '')}` : staticGameAssetUrl(manifestFile);
}

export interface GameTextureManifestEntry {
  file: string;
  /** Pixel region within a shared sprite sheet. */
  frame?: { x: number; y: number; width: number; height: number };
  presentation?: {
    /** Accepted 2:1 source foundation, in native texture pixels. */
    footprintCornersPx?: {left:{x:number;y:number};front:{x:number;y:number};right:{x:number;y:number}};
    doorPx?: {x:number;y:number};
    bodyBoundsPx: { x: number; y: number; width: number; height: number };
    groundContactPx: { x: number; y: number };
    displayHeightPx: number;
  };
  /** Full texture is a flat 2:1 diamond, centered on its world cell. */
  flatTile?: boolean;
  kind: string;
  pixellabTileId?: string;
  pixellabObjectId?: string;
  pixellabCharacterId?: string;
  /** When only one rotation is exported to PNG (e.g. south). */
  rotation?: string;
}

export interface GameTextureManifest {
  version: number;
  /** Native PNG dimensions keyed by physical file; available before download/decode. */
  sourceDimensions?: Record<string, { width: number; height: number }>;
  note?: string;
  assetsRoot?: string;
  worldPresentation?: {
    coherentTerrain?: boolean;
    texturedRoads?: boolean;
    /** Visual tree/bush families by coherent terrain, preserving object identities and collision. */
    biomeObjects?: Record<string, Record<string, readonly string[] | undefined>>;
  };
  textures: Record<string, GameTextureManifestEntry>;
  /** Saved semantic keys reference physical assets; overrides describe a use, not new artwork. */
  useBindings?: Record<string, { assetId: string; presentation?: GameTextureManifestEntry['presentation']; frame?: GameTextureManifestEntry['frame'] }>;
}

const manifestRoots = new WeakMap<GameTextureManifest, string>();
const manifestLeases = new WeakMap<GameTextureManifest, () => void>();
export function releaseGameTextureManifest(manifest: GameTextureManifest): void {
  manifestLeases.get(manifest)?.(); manifestLeases.delete(manifest);
}
async function fetchManifestJson(resolvedUrl: string, signal?: AbortSignal): Promise<GameTextureManifest> {
  const release = releaseAssetPath(resolvedUrl);
  const configuredRelease = releaseAssetPath(gameAssetsBaseUrl() ?? '/') ?? releaseAssetPath(import.meta.env.BASE_URL ?? '/');
  if ((release && !configuredRelease) || (configuredRelease && (!release || release.root !== configuredRelease.root))) throw Error('Manifest differs from configured release generation');
  const pinned = release ? await readReleaseManifest(resolvedUrl, signal) : undefined;
  let manifest: GameTextureManifest;
  try {
    if (pinned) {
      manifest = pinned.value as GameTextureManifest;
      if (manifest?.version !== pinned.manifestVersion) throw Error('Release manifest version mismatch');
    } else {
      const res = await fetch(resolvedUrl, { signal });
      if (!res.ok) throw new Error(`Game manifest fetch failed: ${res.status}`);
      manifest = (await res.json()) as GameTextureManifest;
    }
  } catch (error) { pinned?.release(); throw error; }
  if (signal?.aborted) { pinned?.release(); throw new DOMException('Manifest request cancelled', 'AbortError'); }
  const textures = manifest?.textures;
  if (
    !manifest ||
    textures === null ||
    typeof textures !== 'object' ||
    Array.isArray(textures) ||
    Object.keys(textures).length === 0
  ) {
    pinned?.release();
    throw new Error('Game manifest missing or empty textures');
  }
  if (pinned) {
    if (!Object.values(textures).every(entry => entry && typeof entry.file === 'string' && pinned.paths.has(`game/${entry.file}`))) {
      pinned.release(); throw Error('Incomplete manifest asset descriptors');
    }
    manifestLeases.set(manifest, pinned.release); manifestRoots.set(manifest, pinned.root);
  }
  return manifest;
}

export function resolveGameManifestUrl(): string {
  const base = import.meta.env.BASE_URL ?? '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  const release = releaseAssetPath(gameAssetsBaseUrl() ?? '/') ?? releaseAssetPath(base);
  return release ? `${release.root}game/manifest.json` : `${normalizedBase}assets/game/manifest.json?version=141`;
}

export async function fetchGameTextureManifest(url?: string, signal?: AbortSignal): Promise<GameTextureManifest> {
  return fetchManifestJson(url ?? resolveGameManifestUrl(), signal);
}
